/**
 * Catalog of first-party connectors. The catalog is the single source of
 * truth: nav gating, RBAC scopes, and the Settings → Connectors marketplace
 * all read from here. Adding a connector to the console is a one-line edit
 * to this map — everything else follows.
 */
import type { ConnectorDefinition, ConnectorId } from "@/types/connectors";

export const CONNECTOR_REGISTRY: Record<ConnectorId, ConnectorDefinition> = {
  edr: {
    id: "edr",
    name: "Smplify EDR",
    tagline: "Apple-native endpoint detection & response on the ESF sensor.",
    description:
      "Full-spectrum EDR built on Apple's Endpoint Security Framework. Deployed silently via MDM — no SystemExtensions prompts, no PPPC chasing, no separate agent to roll out.",
    tier: "paid",
    category: "Security",
    scopes: ["edr.read", "edr.manage", "edr.respond", "edr.admin"],
    capabilities: [
      "Real-time process, file, code-signing and login telemetry",
      "Binary authorization (supersedes Santa) with allow / deny / dry-run modes",
      "Quarantine + restore, ransomware pattern detection",
      "mSCP / NIST 800-53 / CIS macOS compliance scoring",
      "Live rule push over SSE — sensors react in ~1s",
    ],
    note: "Replaces and supersedes the Santa connector — all binary allowlisting policies migrate automatically.",
  },
  lighthouse: {
    id: "lighthouse",
    name: "Smplify Lighthouse",
    tagline: "AI agent discovery, inventory & governance.",
    description:
      "Discovers every AI agent — Claude Code, Cursor, Copilot, LangChain, custom in-house, cloud-resident — across endpoint, network, IdP, infra and MCP. Computes effective access, scores risk, and governs through Approval Gates.",
    tier: "paid",
    category: "Security",
    scopes: ["lighthouse.read", "lighthouse.manage", "lighthouse.admin"],
    requires: ["edr"],
    capabilities: [
      "Canonical agent IDs across endpoint / network / IdP / cloud / MCP",
      "Effective access view across all bound principals",
      "Explainable 0–100 risk score with 90-day drift baseline",
      "Sanctioned / Shadow / Unknown / Quarantined taxonomy",
      "Governance via existing Approval Gates — no new enforcement primitive",
    ],
    note: "Rides on the EDR sensor. Enable EDR first.",
  },
  santa: {
    id: "santa",
    name: "Santa",
    tagline: "Open-source binary allowlisting from North Pole Security.",
    description:
      "Free on-ramp for binary allowlisting on macOS. Customers ready for behavioural detection, ransomware patterns, or process-tree lineage should move to Smplify EDR.",
    tier: "free",
    category: "Security",
    scopes: [],
    capabilities: [
      "Binary allowlisting on macOS",
      "Team-id, CDHash, signing-cert subject rules",
      "Monitor / Lockdown modes",
    ],
  },
};

export const CONNECTOR_IDS = Object.keys(CONNECTOR_REGISTRY) as ConnectorId[];

export const isConnectorId = (v: string): v is ConnectorId =>
  Object.prototype.hasOwnProperty.call(CONNECTOR_REGISTRY, v);
